import { useEffect, useState } from 'react';

// The server is authoritative on how much time is left; this only keeps the
// number moving between polls so a session's clock does not appear frozen.
// Every poll re-syncs it, so local drift never accumulates.
//
// Takes SECONDS, not minutes. It used to take minutes and multiply by 60, which
// silently rounded a customer sitting at 59:27 down to 59:00 — the dashboard
// then disagreed with the station's own countdown by up to a minute.
//
// `running` false holds the number still — a paused session's clock has
// stopped on the server, and a countdown that kept moving here would show the
// customer losing time they are not being charged for.
export function useCountdown(
  secondsRemaining: number | null,
  syncKey: string,
  running = true,
): number {
  const [seconds, setSeconds] = useState(() => secondsRemaining ?? 0);

  useEffect(() => {
    setSeconds(secondsRemaining ?? 0);
  }, [secondsRemaining, syncKey]);

  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => {
      setSeconds((current) => (current > 0 ? current - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [running]);

  return seconds;
}

// Seconds until a server-side deadline (an unblocked seat's relock time). The
// deadline is the truth; this just ticks towards it. Null when there is none.
export function useDeadline(deadline: string | null | undefined): number | null {
  const [seconds, setSeconds] = useState<number | null>(() => untilSeconds(deadline));

  useEffect(() => {
    setSeconds(untilSeconds(deadline));
    if (!deadline) return;
    const timer = window.setInterval(() => setSeconds(untilSeconds(deadline)), 1000);
    return () => window.clearInterval(timer);
  }, [deadline]);

  return seconds;
}

function untilSeconds(deadline: string | null | undefined): number | null {
  if (!deadline) return null;
  return Math.max(0, Math.floor((new Date(deadline).getTime() - Date.now()) / 1000));
}

// Time already played, counting up from the server's figure and re-synced on
// every poll — the mirror of useCountdown. The server's seconds_played has the
// pauses taken out, which wall-clock since start_time does not; `running`
// false holds it still while the session is paused.
export function useCountUp(
  seconds: number | null,
  syncKey: string,
  running = true,
): number {
  const [value, setValue] = useState(() => seconds ?? 0);

  useEffect(() => {
    setValue(seconds ?? 0);
  }, [seconds, syncKey]);

  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setValue((current) => current + 1), 1000);
    return () => window.clearInterval(timer);
  }, [running]);

  return value;
}

// Time already played, ticking up from the session's start. The floor card
// shows it beside the countdown, so staff can answer "how long have they been
// on?" without doing the arithmetic from the start time in their head.
//
// Derived from start_time rather than from a server field: the stations list
// only carries what is left, and wall-clock elapsed is exactly what a session
// is billed for.
export function useElapsed(startTime: string): number {
  const [seconds, setSeconds] = useState(() => elapsedSeconds(startTime));

  useEffect(() => {
    setSeconds(elapsedSeconds(startTime));
    const timer = window.setInterval(() => {
      setSeconds(elapsedSeconds(startTime));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [startTime]);

  return seconds;
}

function elapsedSeconds(startTime: string): number {
  return Math.max(
    0,
    Math.floor((Date.now() - new Date(startTime).getTime()) / 1000),
  );
}
